import type { RankingDecision } from '../index.js'
import { TIER_BG_CLASS, TIER_LABELS } from '../../core/index.js'

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function movement(rank: number, inputPosition: number): { text: string; className: string } {
  if (rank < inputPosition) {
    return { text: `↑ ${inputPosition - rank}`, className: 'text-green' }
  }
  if (rank > inputPosition) {
    return { text: `↓ ${rank - inputPosition}`, className: 'text-coral' }
  }
  return { text: '·', className: 'text-ink-mid' }
}

/** Looks a worker's name up from a roster the caller already has; the ranking itself carries ids only. */
export type WorkerNames = ReadonlyMap<string, string>

/**
 * One ranking: the order returned, where each worker sat in the pool the
 * partner sent, and the tier and score that put them there.
 */
export function RankingResult({ decision, names }: { decision: RankingDecision; names?: WorkerNames }) {
  return (
    <div>
      <div className="text-[11px] text-ink-mid mb-2">
        {decision.prioritized ? (
          <>Ordered by tier, then score. Workers equal on both keep the order they were sent in.</>
        ) : (
          <>
            <span className="font-bold text-ink">Prioritization is off for this facility</span>, so the pool is in the order it was sent.
          </>
        )}
      </div>
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="border-b border-marine/15 text-[10px] uppercase tracking-wide text-ink-mid">
            <th className="py-1.5 pr-3 font-bold w-10">Rank</th>
            <th className="py-1.5 pr-3 font-bold">Worker</th>
            <th className="py-1.5 pr-3 font-bold">Tier</th>
            <th className="py-1.5 pr-3 font-bold text-right">Score</th>
            <th className="py-1.5 pr-3 font-bold text-right">Sent as</th>
            <th className="py-1.5 font-bold text-right">Moved</th>
          </tr>
        </thead>
        <tbody>
          {decision.workers.map((worker) => {
            const moved = movement(worker.rank, worker.inputPosition)
            const name = names?.get(worker.workerId)
            return (
              <tr key={worker.workerId} className="border-b border-marine/10">
                <td className="py-1.5 pr-3 font-black text-ink">{worker.rank}</td>
                <td className="py-1.5 pr-3">
                  {/* A worker the facility has no record of has no name: the partner's id is all there is. */}
                  {name ? <div className="font-bold text-ink">{name}</div> : <div className="text-[10px] text-ink-mid font-mono">{worker.workerId}</div>}
                </td>
                <td className="py-1.5 pr-3 text-ink whitespace-nowrap">
                  {worker.tier ? (
                    <span className="inline-flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${TIER_BG_CLASS[worker.tier]}`} />
                      {TIER_LABELS[worker.tier]}
                    </span>
                  ) : (
                    <span className="text-ink-mid">Not on this roster</span>
                  )}
                </td>
                <td className="py-1.5 pr-3 text-ink text-right">{worker.score ?? '—'}</td>
                <td className="py-1.5 pr-3 text-ink-mid text-right">#{worker.inputPosition}</td>
                <td className={`py-1.5 font-bold text-right ${moved.className}`}>{moved.text}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** A facility's recent rankings, newest first; each expands to show its order. */
export function RankingList({ decisions, names }: { decisions: RankingDecision[]; names?: WorkerNames }) {
  if (decisions.length === 0) {
    return <div className="bg-white border border-marine/15 p-4 text-xs text-ink-mid">No partner has asked this facility to rank a pool yet.</div>
  }

  return (
    <div className="bg-white border border-marine/15 divide-y divide-marine/10">
      {decisions.map((decision) => (
        <details key={decision.rankingId} className="group">
          <summary className="px-4 py-2.5 cursor-pointer flex items-center gap-3 text-xs">
            <span className="font-bold text-ink font-mono break-all flex-1">{decision.shiftRef}</span>
            <span className="text-ink-mid whitespace-nowrap">
              {decision.workers.length} {decision.workers.length === 1 ? 'worker' : 'workers'}
            </span>
            {!decision.prioritized && <span className="text-[10px] font-bold text-coral whitespace-nowrap">Not prioritized</span>}
            <span className="text-ink-mid whitespace-nowrap">{formatDateTime(decision.rankedAt)}</span>
          </summary>
          <div className="px-4 pb-4">
            <RankingResult decision={decision} names={names} />
          </div>
        </details>
      ))}
    </div>
  )
}
